import type React from 'react'
import { useCallback, useEffect, useRef, useState } from 'react'

const useDragScroll = (deps: unknown[] = []) => {
  const scrollRef = useRef<HTMLDivElement>(null)
  const [canScrollLeft, setCanScrollLeft] = useState(false)
  const [canScrollRight, setCanScrollRight] = useState(false)

  const isDragging = useRef(false)
  const dragStartX = useRef(0)
  const dragScrollLeft = useRef(0)
  const hasDragged = useRef(false)

  const updateScrollIndicators = useCallback(() => {
    const el = scrollRef.current
    if (!el) return
    setCanScrollLeft(el.scrollLeft > 0)
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1)
  }, [])

  const onMouseDown = useCallback((e: React.MouseEvent) => {
    const el = scrollRef.current
    if (!el) return
    isDragging.current = true
    hasDragged.current = false
    dragStartX.current = e.pageX - el.offsetLeft
    dragScrollLeft.current = el.scrollLeft
    el.style.cursor = 'grabbing'
  }, [])

  const onMouseMove = useCallback((e: React.MouseEvent) => {
    if (!isDragging.current) return
    const el = scrollRef.current
    if (!el) return
    e.preventDefault()
    const x = e.pageX - el.offsetLeft
    const walk = x - dragStartX.current
    if (Math.abs(walk) > 3) hasDragged.current = true
    el.scrollLeft = dragScrollLeft.current - walk
  }, [])

  const onMouseUp = useCallback(() => {
    const el = scrollRef.current
    if (el) el.style.cursor = 'grab'
    isDragging.current = false
  }, [])

  useEffect(() => {
    const el = scrollRef.current
    if (!el) return
    updateScrollIndicators()
    el.addEventListener('scroll', updateScrollIndicators)
    const observer = new ResizeObserver(updateScrollIndicators)
    observer.observe(el)
    return () => {
      el.removeEventListener('scroll', updateScrollIndicators)
      observer.disconnect()
    }
  }, deps)

  return {
    scrollRef,
    canScrollLeft,
    canScrollRight,
    hasDragged,
    onMouseDown,
    onMouseMove,
    onMouseUp,
  }
}

export default useDragScroll
